import { Map } from 'mapbox-gl';
import { DEFAULT_MAP_FOV, ISOMETRIC_MAP_FOV } from './map-config.constants';

type PerspectiveMode = '2d' | '3d';

type EaseToOptions = Parameters<Map['easeTo']>[0];

/**
 * Pitch values for each perspective mode
 */
const PITCH_VALUES: Record<PerspectiveMode, number> = {
  '2d': 0,
  '3d': 60,
} as const;

/**
 * Animation duration for perspective transitions (ms)
 */
const ANIMATION_DURATION = 1000;

/**
 * Threshold for considering pitch values equal (degrees)
 */
const PITCH_EQUALITY_THRESHOLD = 0.1;
const SHIFT_T_PERSPECTIVE_PITCH = 70;

/** Shared duration for the quick camera actions in Design. */
export const QUICK_CAMERA_ANIMATION_DURATION_MS = 250;

/**
 * Pitch at or below this (degrees) counts as a top-down 2D view.
 * Residual tilt from the 2D toggle or a near-nadir drag is still 2D.
 */
export const TOP_DOWN_PITCH_TOLERANCE_DEGREES = 1;

/**
 * True when the camera looks almost straight down.
 * Stacked storeys then occupy the same screen region, so a map click must
 * not change the selected storey.
 */
export const isTopDownPitch = (pitch: number): boolean =>
  Math.abs(pitch) <= TOP_DOWN_PITCH_TOLERANCE_DEGREES;

/** Use an isometric lens near top-down and the normal lens at every other pitch. */
export const getMapFovForPitch = (pitch: number): number =>
  isTopDownPitch(pitch) ? ISOMETRIC_MAP_FOV : DEFAULT_MAP_FOV;

/** Shift+T toggles between exact top-down and the requested steep 3D pitch. */
export const getShiftTPitchTarget = (pitch: number): number =>
  isTopDownPitch(pitch) ? SHIFT_T_PERSPECTIVE_PITCH : 0;

/**
 * Threshold for considering the map to already face north (degrees). A residual
 * fraction of a degree from a drag is not worth a second of animation.
 */
const BEARING_EQUALITY_THRESHOLD = 0.1;

/**
 * Ease-out easing function: t * (2 - t)
 * Provides smooth deceleration at the end of animation
 */
const easeOutEasing = (t: number): number => t * (2 - t);

/** Progress curve used when a custom projection cannot be animated by Mapbox. */
export const getQuickCameraAnimationProgress = (elapsedMs: number): number => {
  const linearProgress = Math.min(
    1,
    Math.max(0, elapsedMs / QUICK_CAMERA_ANIMATION_DURATION_MS),
  );
  return easeOutEasing(linearProgress);
};

/**
 * Facade settle uses the full quick-camera duration for whatever stretch
 * remains between Mapbox's pitch cap and exact 90°.
 * Remaining progress must not shorten that duration.
 */
export const getFacadeSettleProgress = ({
  elapsedMs,
  startProgress,
  targetProgress,
}: {
  elapsedMs: number;
  startProgress: number;
  targetProgress: number;
}): number => {
  const easedProgress = getQuickCameraAnimationProgress(elapsedMs);
  return startProgress + (targetProgress - startProgress) * easedProgress;
};

/**
 * Shared animation shape for the camera moves this module performs.
 * Both directions return to north: the view toggle presents itself as a reset,
 * so a map turned in 2D must not stay turned when it flips to 3D.
 */
const BASE_EASE_OPTIONS: EaseToOptions = {
  bearing: 0,
  animate: true,
  duration: ANIMATION_DURATION,
  easing: easeOutEasing,
};

/**
 * Build easeTo options for changing map perspective, always easing back to
 * north.
 * @param mode Target perspective mode
 * @returns EaseTo options configured for the mode
 */
const buildEaseOptions = (mode: PerspectiveMode): EaseToOptions => ({
  ...BASE_EASE_OPTIONS,
  easeId: 'pitch',
  pitch: PITCH_VALUES[mode],
});

/**
 * Check if the map is already at the target pitch
 * @param map Map instance
 * @param targetMode Target perspective mode
 * @returns true if already at target pitch
 */
const isAtTargetPitch = (map: Map, targetMode: PerspectiveMode): boolean => {
  const currentPitch = map.getPitch();
  const targetPitch = PITCH_VALUES[targetMode];
  return Math.abs(currentPitch - targetPitch) < PITCH_EQUALITY_THRESHOLD;
};

/**
 * Start the pitch animation after ensuring any ongoing animations have stopped.
 * Waits for 'moveend' event to ensure stop() has completed, with a timeout fallback.
 * @param map Map instance
 * @param easeOptions EaseTo options for the animation
 */
const startPitchAnimation = (map: Map, easeOptions: EaseToOptions): void => {
  const startAnimation = () => {
    map.stop(); // Stop any ongoing animations

    // Use requestAnimationFrame to ensure we're not in the middle of a render cycle
    requestAnimationFrame(() => {
      map.easeTo(easeOptions);
    });
  };

  // If we animate during zoom animation, our animation will be overridden.
  map.once('zoomend', startAnimation);

  // Start animation immediately (might not work if zoom animation is in progress)
  startAnimation();
};

/**
 * Change the map's perspective mode with smooth animation.
 * Stops any ongoing animations before starting the new one to prevent conflicts.
 * @param map Map instance
 * @param mode Target perspective mode ('2d' or '3d')
 */
export const setMapPerspectiveMode = (
  map: Map,
  mode: PerspectiveMode,
): void => {
  // Already at the target pitch: the toggle still owes the user north.
  if (isAtTargetPitch(map, mode)) {
    if (Math.abs(map.getBearing()) >= BEARING_EQUALITY_THRESHOLD) {
      map.easeTo(BASE_EASE_OPTIONS);
    }
    return;
  }

  // Build animation options before stopping (needed for the callback)
  const easeOptions = buildEaseOptions(mode);

  // Set up the animation start callback BEFORE calling stop()
  // This ensures we catch the moveend event even if it fires synchronously
  startPitchAnimation(map, easeOptions);

  // Stop any existing animations to prevent conflicts
  // This will trigger 'moveend' which will start the new animation
  map.stop();
};
